/* Schreiber für .map-Dateien (Valve 220). Ebenen entstehen hier erst aus den Eckpunkten. */
import type { Brush, Doc, Face, Vec3 } from '../model/types.ts';
import { cross, dot, len, sub } from '../model/vec.ts';
import { validId } from '../model/ids.ts';
import { faceNormal } from '../model/brush.ts';

const fmt = (x: number): string => {
  const r = Math.round(x * 1000) / 1000;
  return String(Object.is(r, -0) ? 0 : r);
};

/** Drei Punkte einer Fläche, die ihre Ebene mit der richtigen Windung beschreiben (größtes Dreieck) */
export function planePoints(verts: Vec3[], vi: number[]): [Vec3, Vec3, Vec3] {
  const n = faceNormal(verts, vi);
  let best: [Vec3, Vec3, Vec3] | null = null, ba = -1;
  const m = vi.length;
  for (let i = 0; i < m - 2; i++) for (let j = i + 1; j < m - 1; j++) for (let k = j + 1; k < m; k++) {
    const a = verts[vi[i]], b = verts[vi[j]], c = verts[vi[k]];
    const ar = len(cross(sub(b, a), sub(c, a)));
    if (ar > ba) { ba = ar; best = [a, b, c]; }
  }
  let [a, b, c] = best as [Vec3, Vec3, Vec3];
  if (dot(cross(sub(a, b), sub(c, b)), n) < 0) { const t = a; a = c; c = t; }
  return [a, b, c];
}

export function faceLine(b: Brush, f: Face): string {
  const p = planePoints(b.verts, f.vi);
  const pt = (v: Vec3) => '( ' + fmt(v[0]) + ' ' + fmt(v[1]) + ' ' + fmt(v[2]) + ' )';
  return pt(p[0]) + ' ' + pt(p[1]) + ' ' + pt(p[2]) + ' ' + f.tex +
    ' [ ' + fmt(f.ua[0]) + ' ' + fmt(f.ua[1]) + ' ' + fmt(f.ua[2]) + ' ' + fmt(f.uo) + ' ]' +
    ' [ ' + fmt(f.va[0]) + ' ' + fmt(f.va[1]) + ' ' + fmt(f.va[2]) + ' ' + fmt(f.vo) + ' ]' +
    ' ' + fmt(f.rot) + ' ' + fmt(f.xs) + ' ' + fmt(f.ys);
}

export function brushText(b: Brush): string {
  if (b.srcText) return b.srcText;
  return '{\n' + b.faces.map((f) => faceLine(b, f)).join('\n') + '\n}';
}

/** opts.ids === false schreibt ohne ID-Kommentare. */
export function toText(doc: Doc, opts?: { ids?: boolean }): string {
  const out: string[] = [], ids = !(opts && opts.ids === false);
  doc.ents.forEach((e) => {
    if (ids && validId(e.id)) out.push('// gsme-id ' + e.id);
    out.push('{');
    const kv = Object.assign({}, e.kv);
    out.push('"classname" "' + (kv.classname || 'info_target') + '"');
    delete kv.classname;
    Object.keys(kv).forEach((k) => { out.push('"' + k.replace(/"/g, '') + '" "' + String(kv[k]).replace(/"/g, '') + '"'); });
    e.brushes.forEach((b) => { if (ids && validId(b.id)) out.push('// gsme-id ' + b.id); out.push(brushText(b)); });
    out.push('}');
  });
  return out.join('\n') + '\n';
}
